import { useState } from 'react';

import type { DialogProps } from '@radix-ui/react-dialog';
import { CheckmarkCircle } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import { atom, useAtom } from 'jotai';

import { useUnlinkAccount } from 'src/api/queries';
import { Button, Modal, NotificationBox } from 'src/components';

import { Trans } from '@lingui/react/macro';
import { getAuthProviderDisplayName } from './LinkedAccounts';

const unlinkUserAtom = atom<{ id: string; provider: string } | null>(null);

export const useUnlinkIdentity = () => useAtom(unlinkUserAtom);

export const ConfirmUnlinkModal = ({ children, ...props }: DialogProps) => {
  const [identityToUnlink] = useUnlinkIdentity();
  const [open, setOpen] = useState(false);
  const unlinkAccount = useUnlinkAccount();

  const authProvider = identityToUnlink
    ? getAuthProviderDisplayName(identityToUnlink.provider)
    : '';

  return (
    <Modal.Root open={open} onOpenChange={setOpen} {...props}>
      <Modal.Trigger asChild>{children}</Modal.Trigger>

      {identityToUnlink && (
        <Modal.Content data-cy="confirm-unlink-modal">
          {unlinkAccount.isSuccess ? (
            <div className="flex flex-col items-center justify-center">
              <CheckmarkCircle
                size={48}
                color="#009245"
                className="mb-16 shrink-0"
              />
              <Modal.Title asChild>
                <Typography variant="heading-lg" className="mb-32">
                  <Trans>Social login removed</Trans>
                </Typography>
              </Modal.Title>

              <Modal.Description asChild>
                <Typography variant="body-md">
                  <Trans>
                    You&apos;ve removed {authProvider} as a login option. To log
                    in, use your email address and password.
                  </Trans>
                </Typography>
              </Modal.Description>

              <Modal.Close asChild>
                <Button variant="secondary" className="mt-32">
                  <Trans>Continue</Trans>
                </Button>
              </Modal.Close>
            </div>
          ) : (
            <>
              <Modal.Title asChild>
                <Typography variant="heading-lg" className="mb-32">
                  <Trans>Remove social login</Trans>
                </Typography>
              </Modal.Title>

              <Modal.Description asChild>
                <Typography variant="body-md">
                  <Trans>
                    Are you sure you want to remove {authProvider} as a login
                    option? You can still use your email address and password to
                    log in.
                  </Trans>
                </Typography>
              </Modal.Description>

              <div className="mt-32 flex flex-col justify-center gap-16 ls:flex-row">
                <Modal.Close asChild>
                  <Button
                    variant="secondary"
                    size="medium"
                    className="w-full xs:w-fit"
                  >
                    <Trans>Cancel</Trans>
                  </Button>
                </Modal.Close>

                <Button
                  size="medium"
                  className="w-full xs:w-fit"
                  loading={unlinkAccount.isPending}
                  onClick={() => {
                    unlinkAccount.mutate({
                      unlinkUserId: identityToUnlink.id,
                      provider: identityToUnlink.provider,
                    });
                  }}
                >
                  <Trans>Confirm</Trans>
                </Button>

                {unlinkAccount.isError && (
                  <NotificationBox
                    error={unlinkAccount.error}
                    data-cy="confirm-unlink-modal"
                  >
                    <Trans>
                      We couldn&apos;t unlink your account. Please try again
                      later.
                    </Trans>
                    <Modal.Close asChild>
                      <Button>
                        <Trans>Close</Trans>
                      </Button>
                    </Modal.Close>
                  </NotificationBox>
                )}
              </div>
            </>
          )}
        </Modal.Content>
      )}
    </Modal.Root>
  );
};
